import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE } from "@/components/motion/Reveal";

const LINKS = [
  { to: "/", label: "Home", id: "nav-home-link" },
  { to: "/about", label: "About", id: "nav-about-link" },
  { to: "/executive-coaching", label: "Executive Coaching", id: "nav-coaching-link" },
  { to: "/business-development", label: "Business Development", id: "nav-business-link" },
  { to: "/speaking", label: "Speaking", id: "nav-speaking-link" },
  { to: "/contact", label: "Contact", id: "nav-contact-link" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${
          scrolled ? "bg-ivory/85 backdrop-blur-xl border-b border-black/5" : "bg-ivory/60 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-brand-logo" className="flex flex-col leading-none">
            <span className="font-serif text-2xl tracking-tight text-obsidian">Genesis</span>
            <span className="font-mono text-[9px] tracking-[0.35em] uppercase text-gold mt-1">
              Consulting Firm
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop-menu">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.id}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-obsidian/70 hover:text-obsidian"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/consultation"
              data-testid="nav-cta-book-consultation"
              className="hidden md:inline-block bg-obsidian text-ivory px-6 py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-gold hover:text-obsidian transition-colors duration-300"
            >
              Book a Consultation
            </Link>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-obsidian p-2"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed inset-0 z-40 bg-obsidian text-ivory flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`${l.id}-mobile`}
                    className={({ isActive }) =>
                      `font-serif text-4xl py-2 block transition-colors ${isActive ? "text-gold italic" : "text-ivory"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mt-12"
            >
              The Beginning of What's Next
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
